import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Rocket, CheckCircle2, Send, Mail, ArrowRight, Bell } from "lucide-react";
import { APPS, LIVE_APPS, ROADMAP_APPS, cleanName } from "@/apps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white placeholder-white/40 hi-body focus:outline-none focus:border-[#3b9bff]/60 focus:ring-1 focus:ring-[#3b9bff]/40 transition-colors";
const labelCls = "hi-body text-xs uppercase tracking-wider text-white/60 mb-1.5 block";

const Checkbox = ({ checked, onChange, label, testid }) => (
  <label
    data-testid={testid}
    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
      checked ? "border-[#3b9bff]/60 bg-[#3b9bff]/12" : "border-white/12 bg-white/5 hover:border-white/25"
    }`}
  >
    <span
      className={`grid place-items-center w-5 h-5 rounded-md border ${
        checked ? "bg-[#3b9bff] border-[#3b9bff]" : "border-white/30"
      }`}
    >
      {checked && <CheckCircle2 size={13} className="text-white" />}
    </span>
    <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
    <span className="hi-body text-sm text-white/85">{label}</span>
  </label>
);

/* ---------------- Beta Signup Dialog (checkboxes for active apps) ---------------- */
export const BetaSignupDialog = ({ trigger, defaultApps = [] }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", platform: "iOS (TestFlight)" });
  const [apps, setApps] = useState(defaultApps);

  const toggleApp = (n) =>
    setApps((a) => (a.includes(n) ? a.filter((x) => x !== n) : [...a, n]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/beta-signups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, apps }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success("You're on the beta list. Thank you!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", platform: "iOS (TestFlight)" });
    setApps(defaultApps);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(reset, 250); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0b1424] border border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="beta-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="beta-success">
            <CheckCircle2 className="mx-auto text-[#4ade80]" size={44} />
            <h3 className="hi-serif text-2xl text-[#f3ead4] mt-4">You're in!</h3>
            <p className="hi-body text-sm text-white/70 mt-2">
              Thanks for joining. We'll email TestFlight / Play testing details to{" "}
              <span className="text-[#3b9bff]">{form.email}</span>.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="hi-serif text-2xl text-[#f3ead4] flex items-center gap-2">
                <Rocket size={20} className="text-[#c9a24b]" /> Join the Beta
              </DialogTitle>
              <DialogDescription className="hi-body text-white/65">
                Help shape our apps. Choose which you'd like to test on TestFlight (iOS) or Google Play (Android).
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="beta-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Name</label>
                  <input data-testid="beta-name" className={field} placeholder="Your name"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input data-testid="beta-email" type="email" className={field} placeholder="you@example.com"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>

              <div>
                <label className={labelCls}>Apps you'd like to test</label>
                <div className="grid grid-cols-1 gap-2" data-testid="beta-apps">
                  {LIVE_APPS.map((a) => (
                    <Checkbox
                      key={a.id}
                      testid={`beta-app-${a.id}`}
                      label={cleanName(a.name)}
                      checked={apps.includes(cleanName(a.name))}
                      onChange={() => toggleApp(cleanName(a.name))}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Platform</label>
                <select data-testid="beta-platform" className={field} value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                  <option>iOS (TestFlight)</option>
                  <option>Android (Google Play)</option>
                  <option>Both</option>
                </select>
              </div>

              <button type="submit" disabled={loading} data-testid="beta-submit"
                className="hi-cta w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63] disabled:opacity-60">
                {loading ? <Loader2 size={17} className="animate-spin" /> : <Rocket size={17} strokeWidth={2.5} />}
                {loading ? "Submitting…" : "Request Beta Access"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Launch List (pre-registration) ---------------- */
export const LaunchListDialog = ({ trigger, defaultApp = "" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "", app: defaultApp });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.app) {
      toast.error("Please add your first name, email and app.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/launch-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success("You're on the launch list!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setDone(false); setForm({ first_name: "", email: "", app: defaultApp }); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(reset, 250); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0b1424] border border-white/10 text-white max-w-md" data-testid="launch-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="launch-success">
            <CheckCircle2 className="mx-auto text-[#4ade80]" size={44} />
            <h3 className="hi-serif text-2xl text-[#f3ead4] mt-4">You're on the list!</h3>
            <p className="hi-body text-sm text-white/70 mt-2">
              We'll email <span className="text-[#3b9bff]">{form.email}</span> the moment{" "}
              <strong className="text-[#f3ead4]">{form.app}</strong> launches.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="hi-serif text-2xl text-[#f3ead4] flex items-center gap-2">
                <Bell size={20} className="text-[#c9a24b]" /> Join the Launch List
              </DialogTitle>
              <DialogDescription className="hi-body text-white/65">
                Be first to know when this app launches. No spam — just the launch news.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="launch-form">
              <div>
                <label className={labelCls}>First name</label>
                <input data-testid="launch-firstname" className={field} placeholder="Your first name"
                  value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="launch-email" type="email" className={field} placeholder="you@example.com"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>App you're interested in</label>
                <select data-testid="launch-app" className={field} value={form.app}
                  onChange={(e) => setForm({ ...form, app: e.target.value })}>
                  <option value="">Select an app…</option>
                  {ROADMAP_APPS.map((a) => (
                    <option key={a.id} value={cleanName(a.name)}>{cleanName(a.name)}</option>
                  ))}
                </select>
              </div>
              <button type="submit" disabled={loading} data-testid="launch-submit"
                className="hi-cta w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63] disabled:opacity-60">
                {loading ? <Loader2 size={17} className="animate-spin" /> : <Bell size={16} strokeWidth={2.5} />}
                {loading ? "Submitting…" : "Notify Me at Launch"}
              </button>
              <p className="hi-body text-[11px] text-white/45 text-center">Unsubscribe anytime.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Newsletter: segmented capture ---------------- */
const INTERESTS = [
  { key: "Bees", label: "Bees (ApiaryKeeper)" },
  { key: "Miles", label: "Miles (MileageKeeper)" },
  { key: "Legal", label: "Legal (EvidenceKeeper)" },
  { key: "Plants", label: "Plants (GreenKeep)" },
  { key: "Poultry", label: "Poultry (FlockKeeper)" },
  { key: "General News", label: "General News" },
];

export const NewsletterSection = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [interests, setInterests] = useState([]);
  const [beta, setBeta] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (k) => setInterests((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const next = (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setStep(2);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, interests, beta_interest: beta }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success("You're subscribed. Welcome aboard!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" data-testid="newsletter" className="max-w-3xl mx-auto px-5 py-16">
      <div className="hi-card p-8 md:p-10 text-center">
        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#3b9bff]/15 text-[#3b9bff] border border-[#3b9bff]/30 mx-auto">
          <Mail size={22} />
        </span>
        <h2 className="hi-serif text-3xl text-[#f3ead4] mt-5">Stay in the Loop</h2>
        <p className="hi-body text-white/70 mt-2 max-w-xl mx-auto">
          Occasional updates on new apps and features — no noise, just the news you asked for.
        </p>

        {done ? (
          <div className="mt-8" data-testid="newsletter-success">
            <CheckCircle2 className="mx-auto text-[#4ade80]" size={40} />
            <p className="hi-body text-white/80 mt-3">
              Thanks! We'll keep <span className="text-[#3b9bff]">{email}</span> posted on the topics you chose.
            </p>
          </div>
        ) : step === 1 ? (
          <form onSubmit={next} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-step1">
            <input
              data-testid="newsletter-email"
              type="email"
              className={field + " flex-1"}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              type="submit"
              data-testid="newsletter-next"
              className="hi-cta inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63]"
            >
              Continue <ArrowRight size={16} strokeWidth={2.5} />
            </button>
          </form>
        ) : (
          <div className="mt-8 text-left max-w-lg mx-auto" data-testid="newsletter-step2">
            <p className="hi-body text-sm text-white/70 mb-3 text-center">
              What are you interested in? Pick any that apply.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {INTERESTS.map((it) => (
                <Checkbox
                  key={it.key}
                  testid={`interest-${it.key.replace(/\s+/g, "-").toLowerCase()}`}
                  label={it.label}
                  checked={interests.includes(it.key)}
                  onChange={() => toggle(it.key)}
                />
              ))}
            </div>

            <label
              data-testid="newsletter-beta-toggle"
              className="flex items-center gap-3 mt-4 rounded-xl border border-[#c9a24b]/30 bg-[#c9a24b]/10 px-4 py-3 cursor-pointer"
            >
              <span className={`grid place-items-center w-5 h-5 rounded-md border ${beta ? "bg-[#c9a24b] border-[#c9a24b]" : "border-white/30"}`}>
                {beta && <CheckCircle2 size={13} className="text-[#1a2417]" />}
              </span>
              <input type="checkbox" className="sr-only" checked={beta} onChange={() => setBeta((b) => !b)} />
              <span className="hi-body text-sm text-white/90">
                <Rocket size={13} className="inline mr-1 text-[#c9a24b]" />
                I'm interested in <strong>beta testing</strong> (not just news)
              </span>
            </label>

            <button
              onClick={submit}
              disabled={loading}
              data-testid="newsletter-submit"
              className="hi-cta w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63] disabled:opacity-60 mt-5"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} strokeWidth={2.5} />}
              {loading ? "Subscribing…" : "Subscribe"}
            </button>
          </div>
        )}

        <p className="hi-body text-xs text-white/50 mt-6">
          We respect your inbox. <span className="text-white/70">Unsubscribe anytime.</span>
        </p>
      </div>
    </section>
  );
};

/* ---------------- Deletion Request Form ---------------- */
export const DeletionRequestForm = () => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", app: "", reason: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.app) {
      toast.error("Please fill in your name, email and app.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/deletion-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success("Deletion request submitted. We'll confirm by email.");
    } catch {
      toast.error("Something went wrong. Please try again or email us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="hi-card p-8 text-center not-prose" data-testid="delete-success">
        <CheckCircle2 className="mx-auto text-[#4ade80]" size={44} />
        <h3 className="hi-serif text-2xl text-[#f3ead4] mt-4">Request received</h3>
        <p className="hi-body text-sm text-white/70 mt-2">
          We've logged your deletion request for <span className="text-[#3b9bff]">{form.email}</span>.
          You'll receive confirmation within 30 days once your data has been removed.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="hi-card p-6 md:p-7 space-y-4 not-prose" data-testid="delete-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Full name</label>
          <input data-testid="delete-name" className={field} placeholder="Your name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Account email</label>
          <input data-testid="delete-email" type="email" className={field} placeholder="you@example.com"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Which app?</label>
        <select data-testid="delete-app" className={field} value={form.app}
          onChange={(e) => setForm({ ...form, app: e.target.value })}>
          <option value="">Select an app…</option>
          {APPS.map((a) => (
            <option key={a.id} value={cleanName(a.name)}>{cleanName(a.name)}</option>
          ))}
          <option value="All apps">All apps</option>
        </select>
      </div>
      <div>
        <label className={labelCls}>Reason (optional)</label>
        <textarea data-testid="delete-reason" rows={3} className={field + " resize-none"}
          placeholder="Anything you'd like us to know…"
          value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
      </div>
      <button type="submit" disabled={loading} data-testid="delete-submit"
        className="hi-cta w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#d34d4d] text-white hover:bg-[#e26565] disabled:opacity-60">
        {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} strokeWidth={2.5} />}
        {loading ? "Submitting…" : "Submit Deletion Request"}
      </button>
    </form>
  );
};
